<template>
	<div :class="{'page-fixed-height': !activePanel}">
		<div class="sideways-page-one" :class="{'disabled': !activePanel}">
			<!-- BEGIN PAGE BAR -->
			<div class="page-bar">
				<breadcrumb :crumbs="breadcrumbArray"></breadcrumb>
			</div>
			<!-- END PAGE BAR -->
			<!-- BEGIN PAGE TITLE-->
			<h1 class="page-title">UNOgames</h1>
			<!-- END PAGE TITLE-->
			<div class="note note-info">
				<p>Create and manage all UNOgames campaigns.</p>
			</div>
			<button type="button" class="btn blue btn-lg" @click="throttledCreate()">Create a New Campaign</button>
			<div class="margin-top-20">
				<div class="relative-block">
					<loading-screen :show="loadingCampaignData" :color="'#3598dc'" :display="'inline'"></loading-screen>
					<div v-if="loadingCampaignData === false">
						<div class="clearfix">
							<dropdown>
								<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
									Campaign Status<b>: {{ advancedSearch.status }}</b>
									<i class="fa fa-angle-down"></i>
								</button>
								<ul slot="dropdown-menu" class="dropdown-menu">
									<li :class="{'active': advancedSearch.status === 'all'}">
										<a @click="setSearchStatus('all')">All</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'running'}">
										<a @click="setSearchStatus('running')">Running</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'pending'}">
										<a @click="setSearchStatus('pending')">Pending</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'paused'}">
										<a @click="setSearchStatus('paused')">Paused</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'expired'}">
										<a @click="setSearchStatus('expired')">Expired</a>
									</li>
								</ul>
							</dropdown>
							<dropdown>
								<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
									Campaign Type<b>: {{ advancedSearch.type }}</b>
									<i class="fa fa-angle-down"></i>
								</button>
								<ul slot="dropdown-menu" class="dropdown-menu">
									<li :class="{'active': advancedSearch.status === 'all'}">
										<a @click="setSearchType('all')">All</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'ig'}">
										<a @click="setSearchType('ig')">Instagage</a>
									</li>
									<li :class="{'active': advancedSearch.status === 'sw'}">
										<a @click="setSearchType('sw')">Scratch To Win</a>
									</li>
								</ul>
							</dropdown>
							<page-results class="pull-right" :totalResults="totalResults" :activePage="activePage" @pageResults="pageResultsUpdate"></page-results>
						</div>
						<div class="margin-top-20">
							<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
						</div>
						<div class="margin-top-20 unogames-table" v-if="numPages !== 0">
							<div>
								<table class="table table-hover">
									<tbody>
										<template v-for="campaign in campaigns">
											<tr>
												<td class="unogames-table-column-icon">
													<i v-if="campaign.campaign_type === 'sw'" class="fa fa-lg fa-star-half-o"></i>
													<i v-if="campaign.campaign_type === 'ig'" class="fa fa-lg fa-instagram"></i>
												</td>
												<td class="unogames-table-column">
													<p>{{ campaign.name }}</p>
													<p class="font-grey-cascade font-12">Created by: N/A</p>
													<p class="font-grey-cascade font-12">Created on: N/A</p>
												</td>
												<td class="unogames-table-column">
													<div class="uno-progress-bar">
														<div :style="`width: ${calculateProgressWidth(campaign)}`"></div>
													</div>
													<p class="font-12">Starts: {{ filterDateString(campaign.starts_at) }}</p>
													<p class="font-grey-cascade font-12">Ends: {{ filterDateString(campaign.ends_at) }}</p>
												</td>
												<td class="unogames-table-column-icon">
													<dropdown>
														<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
															<i class="fa fa-pencil"></i>
														</button>
														<ul slot="dropdown-menu" class="dropdown-menu">
															<li>
																<a @click="editCampaignDetails(campaign.id)">Edit Campaign Details</a>
															</li>
															<li>
																<a @click="editContestDesign(campaign.id)">Edit Contest Layout</a>
															</li>
															<li v-if="campaign.campaign_type === 'sw'">
																<a @click="editContestPrizes(campaign.id)">Edit Contest Prizes</a>
															</li>
															<li v-if="campaign.campaign_type === 'sw'">
																<a @click="editSkillTestingQuestions(campaign.id)">Edit Skill Testing Questions</a>
															</li>
														</ul>
													</dropdown>
													<i class="fa fa-play clickable" v-if="campaign.status === 'pending'" @click="updateStatus(campaign, 'running')"></i>
													<i class="fa fa-pause clickable" v-if="campaign.status !== 'pending'" @click="updateStatus(campaign, 'pending')"></i>
													<i class="fa fa-line-chart clickable"></i>
													<i class="fa fa-trash-o clickable" @click="deleteCampaignConfirmation(campaign)"></i>
												</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>
						</div>
						<no-results :show="numPages === 0" :type="'campaigns'"></no-results>
						<pagination :passedPage="activePage" :numPages="numPages" @activePageChange="activePageUpdate"></pagination>
					</div>
				</div>
			</div>
		</div>

		<!-- PANEL COMPONENTS START -->
		<div class="sideways-page-two" :class="{'active': !activePanel, 'inactive': activePanel, 'full-width-content-fold': $route.name === 'UNOgames - Edit Contest Layout'}" :style="{height: computedHeight}">
			<div class="sideways-blank-fold">
				<div class="text-center">
					<button type="button" class="btn blue btn-lg margin-bottom-10" @click="closePanel()">Return to this page</button>
				</div>
			</div>
			<create-campaign></create-campaign>
			<edit-campaign-details></edit-campaign-details>
			<edit-contest-layout></edit-contest-layout>
			<edit-contest-prizes></edit-contest-prizes>
			<edit-skill-testing-questions></edit-skill-testing-questions>
		</div>
	</div>
</template>
<script>
import $ from 'jquery'
import Breadcrumb from '../modules/Breadcrumb'
import Dropdown from '../modules/Dropdown'
import LoadingScreen from '../modules/LoadingScreen'
import NoResults from '../modules/NoResults'
import PageResults from '../modules/PageResults'
import Pagination from '../modules/Pagination'
import { debounce, throttle } from 'lodash'
import UNOgames from '../../controllers/UNOgames'
import CreateCampaign from './UNOgames/CreateCampaign'
import EditCampaignDetails from './UNOgames/EditCampaignDetails'
import EditContestLayout from './UNOgames/EditContestLayout'
import EditContestPrizes from './UNOgames/EditContestPrizes'
import EditSkillTestingQuestions from './UNOgames/EditSkillTestingQuestions'
import { mapGetters } from 'vuex'

/**
 * UNOgames module is a page for the UNOgames Campaign Dashboard.
 * @class UNOgames
 * @since 0.0.9
 */

export default {
	data () {
		return {
			activePanel: true,
			breadcrumbArray: [
				{name: 'UNOgames', link: false}
			],
			computedHeight: 0,
			advancedSearch: {
				status: 'all',
				type: 'all'
			},
			numPages: 0,
			totalResults: 0,
			activePage: 1,
			loadingCampaignData: false,
			campaigns: []
		}
	},
	/**
	 * Run on `created` to initialize the throttle events and to check the current process step.
	 * @function
	 * @returns {undefined}
	 * @memberof UNOgames
	 * @version 0.0.9
	 */
	created () {
		this.throttledCreate = throttle(() => {
			this.createCampaign()
		}, 500, { 'trailing': false })

		if (this.$route.name !== 'UNOgames') {
			this.activePanel = false
		}

		let _this = this

		this.computeHeight()

		$(window).on('resize', debounce(() => {
			_this.computeHeight()
		}, 200))
	},
	computed: {
		...mapGetters([
			'activeBusinessId',
			'activeLocationId'
		])
	},
	methods: {
		/**
		 * To compute height to display based on window height and navbar
		 * @function
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		computeHeight () {
			const height = $(window).height() - 50
			this.computedHeight = height + 'px'
		},
		/**
		 * To close anything active in the side panel by redirecting to the roles dashboard route
		 * @function
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		closePanel () {
			this.$router.push('/app/uno_games')
		},
		/**
		 * To catch updates from the PageResults component when the number of page results is updated.
		 * @function
		 * @param {integer} val - The number of page results to be returned.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		pageResultsUpdate (val) {
			if (parseInt(this.pageResultsValue) !== parseInt(val)) {
				this.pageResultsValue = val
				if (parseInt(this.activePage) === 1) {
					this.getCampaigns(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To update the currently active pagination page.
		 * @function
		 * @param {integer} val - An integer representing the page number that we are updating to.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		activePageUpdate (val) {
			if (parseInt(this.activePage) !== parseInt(val)) {
				this.activePage = val
				this.getCampaigns(this.activePage)
			}
		},
		/**
		 * To get a list of campaigns for the current page.
		 * @function
		 * @param {integer} pageNumber - The current page that we are retrieving results for.
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		getCampaigns (pageNumber) {
			this.loadingCampaignData = true

			let _this = this
			const businessId = this.activeBusinessId
			const locationId = this.activeLocationId
			const searchCriteria = {campaign_type: this.advancedSearch.type, status: this.advancedSearch.status}

			return UNOgames.getCampaigns(businessId, locationId, pageNumber, this.pageResultsValue, searchCriteria).then(response => {
				_this.campaigns = response.payload.campaigns
				_this.numPages = response.payload.number_of_pages
				_this.totalResults = response.payload.number_of_records
				_this.loadingCampaignData = false
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'campaigns_not_found':
						_this.numPages = 0
						_this.totalResults = 0
						break

					default:
						// No default
					}
				}
				_this.loadingCampaignData = false
			}))
		},
		/**
		 * To format the passed in date to return the proper string.
		 * @function
		 * @param {string} date - The date that we are formatting.
		 * @returns {string} - A formatted date string.
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		filterDateString (date) {
			let fullDate = new Date(date)
			let formattedDate = ''
			let formattedTime = ''

			formattedDate = `${fullDate.getFullYear()}-${(fullDate.getMonth() + 1 < 10) ? ('0' + (fullDate.getMonth() + 1)) : fullDate.getMonth() + 1}-${(fullDate.getDate() < 10) ? ('0' + (fullDate.getDate())) : fullDate.getDate()}`
			formattedTime = `${(fullDate.getHours() > 12) ? fullDate.getHours() - 12 : fullDate.getHours()}:${fullDate.getMinutes()} ${(fullDate.getHours() > 12 & fullDate.getHours() !== 24) ? 'PM' : 'AM'}`
			return `${formattedDate} at ${formattedTime}`
		},
		/**
		 * To calculate the percentage of the campaign that has been completed.
		 * @function
		 * @param {object} campaign - The campaign to be calculated.
		 * @returns {string} - The campaigns complete percentage.
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		calculateProgressWidth (campaign) {
			let percent = 0
			let startDate = new Date(campaign.starts_at)
			let endDate = new Date(campaign.ends_at)
			let campaignLength = endDate - startDate
			let currentDistance = new Date() - startDate

			if (currentDistance > 0 && currentDistance < campaignLength) {
				percent = (currentDistance / campaignLength) * 100
			} else if (currentDistance > campaignLength) {
				percent = 100
			}
			return percent + '%'
		},
		/**
		 * To initialize the create campaign process by redirecting to the create campaign route.
		 * @function
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		createCampaign () {
			this.$router.push('/app/uno_games/create_campaign')
		},
		/**
		 * To initialize the edit campaign details process by redirecting to the edit campaign details route.
		 * @function
		 * @param {integer} campaignId - The campaign to be edited.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		editCampaignDetails (campaignId) {
			this.$router.push('/app/uno_games/edit_campaign_details/' + campaignId)
		},
		/**
		 * To initialize the edit contest layout process by redirecting to the edit contest layout route.
		 * @function
		 * @param {integer} campaignId - The campaign to be edited.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		editContestDesign (campaignId) {
			this.$router.push('/app/uno_games/edit_contest_layout/' + campaignId)
		},
		/**
		 * To initialize the edit contest prizes process by redirecting to the edit contest prizes route.
		 * @function
		 * @param {integer} campaignId - The campaign to be edited.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		editContestPrizes (campaignId) {
			this.$router.push('/app/uno_games/edit_contest_prizes/' + campaignId)
		},
		/**
		 * To initialize the edit skill testing questions process by redirecting to the edit skill testing questions route.
		 * @function
		 * @param {integer} campaignId - The campaign to be edited.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		editSkillTestingQuestions (campaignId) {
			this.$router.push('/app/uno_games/edit_skill_testing_questions/' + campaignId)
		},
		/**
		 * To update the advanced search status and make a backend call to pull the filtered data.
		 * @function
		 * @param {string} status - The status that the user wants to sort by.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		setSearchStatus (status) {
			if (status !== this.advancedSearch.status) {
				this.advancedSearch.status = status
				if (parseInt(this.activePage) === 1) {
					this.getCampaigns(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To update the advanced search type and make a backend call to pull the filtered data.
		 * @function
		 * @param {string} type - The type that the user wants to sort by.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		setSearchType (type) {
			if (type !== this.advancedSearch.type) {
				this.advancedSearch.type = type
				if (parseInt(this.activePage) === 1) {
					this.getCampaigns(1)
				} else {
					this.activePageUpdate(1)
				}
			}
		},
		/**
		 * To delete a campaign.
		 * @function
		 * @param {object} campaign - The campaign to be deleted.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		deleteCampaignConfirmation (campaign) {
			let _this = this
			let buttonText = 'Delete Campaign'
			let messageText = 'You won\'t be able to reverse this action.'
			let successMessage = '<b>' + campaign.name + '</b> has been deleted.'

			this.$swal({
				title: 'Are you sure?',
				html: messageText,
				type: 'warning',
				showCancelButton: true,
				confirmButtonText: buttonText,
				confirmButtonColor: '#d9534f',
				cancelButtonText: 'Cancel',
				allowOutsideClick: false,
				allowEscapeKey: false,
				allowEnterKey: false,
				showLoaderOnConfirm: true,
				preConfirm () {
					return new Promise((resolve, reject) => {
						UNOgames.deleteCampaign(_this.activeLocationId, campaign.id).then(() => {
							resolve('campaign_deleted')
						}).catch(_this.$root.errorWrapper(e => {
							if (e.responseJSON) {
								switch (e.responseJSON.declaration) {
								}
							}
						}))
					})
				}
			}).then(response => {
				_this.$swal({
					title: 'Success',
					html: successMessage,
					type: 'success',
					showCancelButton: true
				}).then(() => {
					_this.getCampaigns(_this.activePage)
				}, dismiss => {
					_this.getCampaigns(_this.activePage)
				})
			}, dismiss => {
				// Do nothing on dismiss
			})
		},
		/**
		 * To update the status of a campaign.
		 * @function
		 * @param {object} campaign - The campaign to be updated.
		 * @param {string} status - The status to be set.
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		updateStatus (campaign, status) {
			let _this = this
			const locationId = this.activeLocationId

			return UNOgames.updateCampaignStatus(locationId, campaign.id, status).then(response => {
				campaign.status = status
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'missing_components':
						let payload = e.responseJSON.payload.components_attached
						if (!payload.text2win) {
							_this.$swal({
								title: 'Error',
								html: 'There seems to be a fatal error with your campaign. Try deleting and recreating the campaign.',
								type: 'warning'
							})
						} else if (!payload.contest) {
							_this.$swal({
								title: 'Error',
								html: 'You cannot run a campaign without creating a contest layout.',
								type: 'warning',
								showCancelButton: true,
								confirmButtonText: 'Create Contest Layout',
								confirmButtonColor: '#3598dc',
								cancelButtonText: 'Cancel',
								allowOutsideClick: false,
								allowEscapeKey: false,
								allowEnterKey: false,
								showLoaderOnConfirm: true
							}).then(() => {
								_this.editContestDesign(campaign.id)
							})
						} else {
							if (campaign.campaign_type === 'ig') {
								if (!payload.instagram_credentials) {
									_this.$swal({
										title: 'Error',
										html: 'You cannot run a campaign without proper Instagram credentials.',
										type: 'warning'
									})
								}
							} else if (campaign.campaign_type === 'sw') {
								if (!payload.prize) {
									_this.$swal({
										title: 'Error',
										html: 'You cannot run a campaign without creating prizes.',
										type: 'warning',
										showCancelButton: true,
										confirmButtonText: 'Create Contest Prizes',
										confirmButtonColor: '#3598dc',
										cancelButtonText: 'Cancel',
										allowOutsideClick: false,
										allowEscapeKey: false,
										allowEnterKey: false,
										showLoaderOnConfirm: true
									}).then(() => {
										_this.editContestPrizes(campaign.id)
									})
								}
							}
						}
						break

					default:
						// No default
					}
				}
			}))
		}
	},
	components: {
		Breadcrumb,
		Dropdown,
		LoadingScreen,
		NoResults,
		PageResults,
		Pagination,
		CreateCampaign,
		EditCampaignDetails,
		EditContestLayout,
		EditContestPrizes,
		EditSkillTestingQuestions
	},
	watch: {
		/**
		 * Watch `$route` so that we can check if we are on the proper panel after each change.
		 * @function
		 * @returns {undefined}
		 * @memberof UNOgames
		 * @version 0.0.9
		 */
		'$route' () {
			if (this.$route.name !== 'UNOgames') {
				this.activePanel = false
			} else {
				this.activePanel = true
				this.getCampaigns(this.activePage)
			}
		}
	}
}
</script>
<style>
.unogames-table {
	max-width: 800px;
	margin: 0 auto;
}
.unogames-table-column > p {
	margin: 0;
}
.unogames-table-column > p.font-12 {
	font-size: 12px;
}
.table td.unogames-table-column-icon {
	padding-top: 24px;
	text-align: center;
}
.table td.unogames-table-column-icon i {
	margin: 0 10px;
}
.uno-progress-bar {
	width: 100%;
	height: 3px;
	background-color: #E9EDEF;
	margin-bottom: 9px;
	margin-top: 8px;
}
.uno-progress-bar > div {
	background-color: #26C281;
	height: 3px;
}
</style>



// WEBPACK FOOTER //
// UNOgames.vue?a63029fc